import {
  DndContext,
  closestCenter,
  DragOverlay,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Group, MantineStyleProps, Text, useMantineTheme } from "@mantine/core"
import { IconGripVertical, IconX } from "@tabler/icons-react"
import cx from "clsx"
import { useEffect, ReactNode, useState } from "react"

import { ActionIcon } from "../../../atoms/ActionIcons/ActionIcon"
import { useStableListState } from "../../../utils/useStableListState"

import classes from "./DragNDrop.module.css"

export type DraggableItem = {
  id: string
  position?: number
  label: string | ReactNode
  cta?: ReactNode
  itemProps?: MantineStyleProps
}

export type DraggableLocation = {
  droppableId: string
  index: number
}

export type DragEndResult = {
  destination?: DraggableLocation | null
  item: DraggableItem
}

export type DragNDropProps = {
  data: DraggableItem[]
  onDragEnd?: (result: DragEndResult) => void
  draggableHandle?: boolean
  deletable?: boolean
  onDelete?: (id: string) => void
  beforeDelete?: (id: string) => Promise<boolean>
  showPosition?: boolean
  width?: string
}

type SortableItemProps = {
  item: DraggableItem
  index: number
  draggableHandle: boolean
  deletable: boolean
  onDelete?: (id: string) => void
  beforeDelete?: (id: string) => Promise<boolean>
  showPosition: boolean
  dispatch: any
  state: DraggableItem[]
}

const SortableItem = ({
  item,
  index,
  draggableHandle,
  deletable,
  onDelete,
  beforeDelete,
  showPosition,
  dispatch,
  state,
}: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  const handleProps = { ...attributes, ...listeners }
  const theme = useMantineTheme()
  return (
    <Group
      ref={setNodeRef}
      style={style}
      className={cx(classes.item, {
        [classes.itemDragging]: isDragging,
      })}
      {...(!draggableHandle && handleProps)}
      {...item.itemProps}
    >
      <Group gap="xs">
        {draggableHandle && (
          <div {...handleProps} className={classes.dragHandle}>
            <IconGripVertical size={14} stroke={1.5} />
          </div>
        )}
        {showPosition && item.position && <Text>{item.position}</Text>}
        {typeof item.label === "string" ? (
          <Text>{item.label}</Text>
        ) : (
          item.label
        )}
      </Group>
      <Group gap={"xs"}>
        {item.cta && item.cta}
        {deletable && (
          <ActionIcon
            key={item.id}
            size={theme.other.icon.size}
            icon={<IconX />}
            onClick={async () => {
              const confirmed = beforeDelete
                ? await beforeDelete(state[index].id)
                : true
              if (!confirmed) return
              dispatch?.({ type: "REMOVE", payload: [index] })
              dispatch?.({
                type: "APPLY",
                payload: (item: DraggableItem, index?: number) => {
                  if (index === undefined) return item
                  return {
                    ...item,
                    position: index + 1,
                  }
                },
              })
              onDelete?.(state[index].id)
            }}
          />
        )}
      </Group>
    </Group>
  )
}

export function DragNDrop({
  data,
  onDragEnd,
  draggableHandle = true,
  deletable = false,
  onDelete,
  beforeDelete,
  showPosition = false,
  width = "100%",
}: DragNDropProps) {
  const [state, dispatch] = useStableListState(
    showPosition
      ? data.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      : data
  )

  const [activeId, setActiveId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  useEffect(() => {
    const newState = showPosition
      ? data.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      : data
    dispatch?.({ type: "SET_STATE", payload: newState })
  }, [data, showPosition, dispatch])

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    setActiveId(null)

    if (!over || active.id === over.id) return

    const oldIndex = state.findIndex((item) => item.id === active.id)
    const newIndex = state.findIndex((item) => item.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      onDragEnd?.({
        item: state[oldIndex],
        destination: {
          droppableId: "dnd-list",
          index: newIndex,
        },
      })

      dispatch?.({
        type: "REORDER",
        payload: { from: oldIndex, to: newIndex },
      })

      dispatch?.({
        type: "APPLY",
        payload: (item: DraggableItem, index?: number) => {
          if (index === undefined) return item
          return {
            ...item,
            position: index + 1,
          }
        },
      })
    }
  }

  const activeItem = activeId
    ? state.find((item) => item.id === activeId)
    : null

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={state.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div style={{ width }}>
          {state.map((item, index) => (
            <SortableItem
              key={item.id}
              item={item}
              index={index}
              draggableHandle={draggableHandle}
              deletable={deletable}
              onDelete={onDelete}
              beforeDelete={beforeDelete}
              showPosition={showPosition}
              dispatch={dispatch}
              state={state}
            />
          ))}
        </div>
      </SortableContext>

      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          <Group
            className={classes.item}
            style={{
              background: "white",
              boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              cursor: "grabbing",
            }}
          >
            <Group gap="xs">
              <IconGripVertical size={14} stroke={1.5} />
              {typeof activeItem.label === "string" ? (
                <Text>{activeItem.label}</Text>
              ) : (
                activeItem.label
              )}
            </Group>
            {activeItem.cta}
          </Group>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
